<template>
  <div :id="`bulk-prospect-action-cta-${source}`" class="flex gap-2">
    <div :class="{ 'cursor-not-allowed': !hasListAccess }">
      <AddToListDropdown
        :source="source"
        :entity-lists="entityLists"
        :show-entity-create="showEntityCreate"
        :disabled="isListDropdownDisabled"
        @entity-list-created="handleEntityListCreated"
        @update:show-entity-create="showEntityCreate = $event"
      />
    </div>

    <div :class="{ 'cursor-not-allowed': !hasSequenceAccess }">
      <AddToSequenceDropdown
        v-if="isSequenceEnabled"
        :disabled="isSequenceDropdownDisabled"
        @sequence-selected="handleSequenceSelected"
        @create-sequence="handleSequenceCreated"
      />
    </div>

    <a-button
      v-if="showDelete"
      class="secondary_small flex items-center gap-1"
      :disabled="isDeleteDisabled"
      @click="handleDelete"
    >
      <ThemeImage :icon="imageNames.DELETE_GREY" class="size-4" />
      Delete
    </a-button>

    <a-button
      class="primary_small"
      @click="handleAction"
      :disabled="isActionButtonDisabled"
    >
      {{ actionButtonText }}
    </a-button>

    <CreateSequenceModal
      :width="'500px'"
      :title="`Create New ${featureNames.SEQUENCES}`"
      v-model:visible="showSequenceCreate"
      @sequence-created="handleSequenceCreatedFromModal"
      :add-action="true"
      :show-message="false"
      :navigate-after-creation="false"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import AddToListDropdown from "@/src/components/custom_table_components/AddToListDropdown.vue";
import AddToSequenceDropdown from "@/src/components/custom_table_components/AddToSequenceDropdown.vue";
import CreateSequenceModal from "@/src/views/sequence/actions/CreateSequenceModal.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { toGetAllEntityLists } from "@/src/utils/utils.js";
import store from "@/src/store/index.js";
import { featureNames } from "@/src/constants/globalConstants";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  numberOfSelected: { type: Number, default: 0 },
  actionButtonText: { type: String, default: "Save" },
  entityPermission: { type: Boolean, default: true },
  isSequenceEnabled: { type: Boolean, default: true },
  showDelete: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:numberOfSelected",
  "data-imported",
  "trigger-add",
  "save-to-list",
  "add-to-sequence",
  "delete",
]);

const entityLists = ref([]);
const showEntityCreate = ref(false);
const showSequenceCreate = ref(false);

const hasListAccess = computed(() => store.getters["rbac/hasListsAccess"]);
const hasSequenceAccess = computed(
  () => store.getters["rbac/hasSequencesAccess"],
);

const isListDropdownDisabled = computed(
  () => props.numberOfSelected <= 0 || !hasListAccess.value,
);

const isSequenceDropdownDisabled = computed(
  () => props.numberOfSelected <= 0 || !hasSequenceAccess.value,
);

const isActionButtonDisabled = computed(
  () => props.numberOfSelected <= 0 || !props.entityPermission,
);

const isDeleteDisabled = computed(() => props.numberOfSelected <= 0);

const handleDelete = () => {
  emit("delete");
};

const handleAction = () => {
  emit("trigger-add");
};

const handleEntityListCreated = async ({ listId }) => {
  let listName = null;
  try {
    const response = await DashboardService.getEntityListByID(listId, 1, 0);
    if (response.success) {
      listName = response.list_name;
    }
  } catch (error) {
    console.error("Error fetching list name:", error);
  }

  await getEntityList();

  emit("save-to-list", { listId, listName });
};

const handleSequenceSelected = ({ sequenceId }) => {
  emit("add-to-sequence", { sequenceId, isNewlyCreated: false });
};

const handleSequenceCreated = () => {
  showSequenceCreate.value = true;
};

const handleSequenceCreatedFromModal = ({ sequenceId }) => {
  showSequenceCreate.value = false;
  emit("add-to-sequence", { sequenceId, isNewlyCreated: true });
};

const getEntityList = async () => {
  try {
    const data = await toGetAllEntityLists(props.source);
    entityLists.value = data || [];
  } catch (error) {
    console.error("Error fetching entity lists:", error);
    entityLists.value = [];
  }
};

onMounted(() => {
  getEntityList();
});
</script>
